import React, { ReactNode, useEffect, useState } from "react";
import { PlayerCardData, PlayerClubCountryInfoData, PlayerClubInfoData } from "../styled-components/PlayerCard.Styles";
import { IClubFinancialInfo, ITeams } from "../types/IPlayerDetail";
import LabelNumber from "../styled-components/LabelNumber";
import LabelText from "../ui/LabelText";
import styled from "styled-components";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import IApiResponse from "../types/IApiResponse";
import handleApiResponse from "../utils/handleApiResponse";
import apiUrls from "../config/apiUrls";
import { useNavigate } from "react-router-dom";
import { fetchData } from "../services/api";
import { PlayerSystemFitData } from "../styled-components/PlayerCard.Styles";
import EmptyDataButton from "../ui/EmptyDataButton";
import noImageClub from "../assets/icons/noImageClub.svg";
import noImageFlag from "../assets/icons/noImageFlag.svg";
import { ModulePitch } from "./ModulePitch";
import { useLoading } from "../contexts/LoadingContext";
import BookmarkBorderOutlinedIcon from "@mui/icons-material/BookmarkBorderOutlined";
import { addUpdateClubSortListData } from "./ClubComparison/addOrUpdateClubSortlist";
import { addUpdateSortListData } from "./PlayerComparison/addOrUpdateSortList";
import { useAuth } from "../contexts/AuthContext";
import SecondaryButton from "../ui/SecondaryButton";
import PWNotification from "./PWNotification";
import { getLeaguePositionDetails } from "../utils/clubFormationsFunctions";
import CustomizedTooltips from "../ui/Tooltip";
import { useTheme } from "../contexts/ThemeContext";
import { formatNumberToKAndM } from "../utils/formatNumberToKandM";


interface Props {
  clubData: ITeams;
  isOpen: boolean;
  onToggle: (event: React.MouseEvent) => void;
  playerData?: { playerId: number; playerClubURL: string };
  type?: string;
}

const StyledIcon = styled.div`
  margin-left: 15px;
  padding-left: 15px;
  border-left: 2px solid ${({ theme }) => theme.playerDataBorder};
  height: 40px;
  align-content: center;
`;

const TableRow = styled.div`
  border-bottom: 1px solid ${({ theme }) => theme.playerDataBorder};
`;

const ClubsInfo: React.FC<Props> = ({ clubData, isOpen, onToggle, playerData, type }) => {
  const {theme} = useTheme();
    const navigate = useNavigate();
    const { userData } = useAuth();
    const { startLoading, stopLoading } = useLoading();
    const [clubFinancialData, setClubFinancialData] = useState<IClubFinancialInfo>();
    const [toggleAddSimilarButton, setIsToggleAddSimilarButton] = useState<boolean>();
    const [addedClubShortList, setAddedClubShortList] = useState<boolean>(false);
    const [displayCustomNotification, setDisplayCustomNotification] = useState<boolean>(false);
    const [customNotificationContent, setCustomNotificationContent] = useState<ReactNode>(null);

  useEffect(()=>{
    setIsToggleAddSimilarButton(clubData.isTeamInShortlist!)
  },[])

  useEffect(() => {
    const fetchClubFinancialData = async () => {
      if (!clubData?.team?.teamId) return;
      try {
        startLoading();
        const response: IApiResponse<IClubFinancialInfo> = await fetchData(
          `${apiUrls["GetTeamFinancialDetail"]}?teamId=${clubData?.team?.teamId}`
        );
        handleApiResponse(
          response,
          (teamsData) => {
            if (teamsData) {
              setClubFinancialData(teamsData); // Update state only if the data is available
            }
          },
          (error) => {
            console.error("Error fetching club card Data:", error);
          }
        );
      } catch (error) {
        console.error("Error fetching club financial data:", error);
      } finally {
        stopLoading();
      }
    };
    if (isOpen) {
      fetchClubFinancialData();
    }
  }, [isOpen, clubData?.team?.teamId]);

  const getBackgroundColor = (performanceValue?: number): string => {
    if (performanceValue == null) {
      return "none";
    }
    if (performanceValue >= 0 && performanceValue <= 19) {
      return "var(--UI-red-500, #FF3864)";
    }
    if (performanceValue >= 20 && performanceValue <= 39) {
      return "var(--Brand-orange-500, #FF7800)";
    }
    if (performanceValue >= 40 && performanceValue <= 59) {
      return "var(--UI-yellow-500, #F2E94E)";
    }
    if (performanceValue >= 60 && performanceValue <= 100) {
      return "var(--Brand-green-2-500, #4AF075)";
    }
    return "red";
  };

  const valueStyle = {
    color: (clubFinancialData?.profitLoss || 0) > 0 ? "#00D1A8" : "#FF3864",
  };

  const perfomaceuplift = {
    color: (clubData?.performanceUplift || 0) > 0 ? "#00D1A8" : "#FF3864",
  };
  const chemistryUplift = clubData.playerChemistryWithSystemFit.newSystemFit - clubData.playerChemistryWithSystemFit.oldSystemFit;

  const systemFit ={
    color : (chemistryUplift || 0) > 0  ? "#00D1A8" : "#FF3864",
  }

  const stylistic = { color : (clubData?.stylisticFitAbsoluteDiffInPercentage! > 0) ? "#00D1A8" : "#FF3864", }

  // const backgroundColor = getBackgroundColor(clubData?.team.systemFit);

  const handleAddPlayer = () => {
    setIsToggleAddSimilarButton(!toggleAddSimilarButton);
    if (toggleAddSimilarButton) {
      setAddedClubShortList(false);
      addUpdateClubSortListData(
        userData,
        [clubData?.team.teamId!],
        startLoading,
        stopLoading,
        setDisplayCustomNotification,
        setCustomNotificationContent,
        true,
        navigate
      );
    } else {
      setAddedClubShortList(true);
      addUpdateClubSortListData(
        userData,
        [clubData?.team?.teamId!],
        startLoading,
        stopLoading,
        setDisplayCustomNotification,
        setCustomNotificationContent,
        false,
        navigate
      );
    }
  };

  const handleActions = () => {
    setIsToggleAddSimilarButton(!toggleAddSimilarButton);
    if (!addedClubShortList) {
      addUpdateClubSortListData(
        userData,
        [clubData?.team?.teamId!],
        startLoading,
        stopLoading,
        setDisplayCustomNotification,
        setCustomNotificationContent,
        false,
        navigate
      );
    }
  };

  useEffect(() => {
    setCustomNotificationContent(
      <div className="notification">
        <p className="sortlist-notofication-content">
          <BookmarkBorderOutlinedIcon className="icon" />
          <LabelText className="squad-chart-tooltip notification-content">
            {addedClubShortList ? "Club added to your shortlist " : "Club removed from your shortlist"}
          </LabelText>
          {!addedClubShortList ? (
                <LabelText className="player-given-name notification-content" style={{ cursor: "pointer" }} onClick={() => handleActions()}>
                  Undo
                </LabelText>
              ) : (
                <LabelText
                  className="player-given-name notification-content"
                  style={{ cursor: "pointer" }}
                  onClick={() => navigate("/shortlists")}
                >
                  View shortlist
                </LabelText>
              )}
        </p>
      </div>
    );
  }, [addedClubShortList]);

  // const { formattedPosition, formattedDifference, differenceColor, tooltipText } = getLeaguePositionDetails(clubData!);
  

  return (
    <>
      <PlayerCardData>
        <div className="player-image-content">
          <div
            className="club-badge"
            onClick={() => {
              navigate(`/club/${clubData.team.teamId}`);
            }}
          >
            <img src={clubData?.team.teamLogoURL ? clubData.team.teamLogoURL : noImageClub} alt="club-image" />
          </div>
          <div
            className="player-club-detail"
            onClick={() => {
              navigate(`/club/${clubData.team.teamId}`);
            }}
          >
            <div className="clubName-position-container">
                                        {/* <LabelText className='player-detail-given-name' style={{ color: theme.NumberColor }}>{clubData?.competitionName+ "  • Currently " + formattedPosition} </LabelText> */}
                                        {/* <CustomizedTooltips title={tooltipText}>
                                            <div className="expected-league-container"><LabelNumber style={{ color: differenceColor }}>{formattedDifference}</LabelNumber></div>
                                        </CustomizedTooltips> */}
                                </div>
            {/* <LabelText className="club-league">{clubData?.competitionName}</LabelText> */}
            <LabelText className="player-detail-family-name">{clubData?.team?.teamName}</LabelText>
            <div style={{marginLeft:'500px',marginTop:'-6px',justifySelf:'center'}}>
              <LabelNumber className="stat-row-value">Manager:</LabelNumber>
              <LabelText className="player-given-name">{clubData?.team?.manager}</LabelText>
            </div>
            
          </div>
          <div className="actions-btn">
          <SecondaryButton
                    label={isOpen && (toggleAddSimilarButton ? "Added to shortlist" : "Add to shortlist")}
                    iconType="bookmark"
                    onClick={() => handleAddPlayer()}
                    isSelected={toggleAddSimilarButton || addedClubShortList}
                  />
            <StyledIcon>
              {isOpen ? (
                <KeyboardArrowUpIcon sx={{ fontSize: "24px" }} onClick={onToggle} />
              ) : (
                <KeyboardArrowDownIcon sx={{ fontSize: "24px" }} onClick={onToggle} />
              )}
            </StyledIcon>
            
          </div>
        </div>
        <div className="player-large-data-details">
          <PlayerClubCountryInfoData>
            <PlayerClubInfoData country="country" className="player-descriptive-data">
              <LabelNumber className="descriptive-large-text">Country</LabelNumber>
              <LabelNumber className="descriptive-large-value">{clubData?.team?.countryName || ""}</LabelNumber>
            </PlayerClubInfoData>
            <img
              src={clubData.team.countryCode ? `/assets/country_flags/${clubData?.team?.countryCode}.svg` : noImageFlag}
              alt="Club"
              className="country-image"
            />
          </PlayerClubCountryInfoData>
          <PlayerClubInfoData className="player-descriptive-data">
            <LabelNumber className="descriptive-large-text">League Pos.</LabelNumber>
            <LabelNumber className="descriptive-large-value">{clubData?.team?.currentLeaguePosition}</LabelNumber>
          </PlayerClubInfoData>
          <PlayerClubInfoData className="player-descriptive-data">
            <LabelNumber className="descriptive-large-text">HIGHEST WAGE PW</LabelNumber>
            <LabelNumber className="descriptive-large-value">
              {clubData?.team?.highestWagePerWeek! === null || clubData?.team?.highestWagePerWeek === 0 ? (
                <EmptyDataButton label="Data not provided by club" />
              ) : (
                `€${formatNumberToKAndM(clubData?.team?.highestWagePerWeek)}`
              )}
            </LabelNumber>
          </PlayerClubInfoData>
          <PlayerClubInfoData className="player-descriptive-data">
            <LabelNumber className="descriptive-large-text">AVERAGE WAGE PW</LabelNumber>
            <LabelNumber className="descriptive-large-value">
              {clubData?.team?.averageWagePerWeek === null || clubData?.team?.averageWagePerWeek === 0 ? (
                <EmptyDataButton label="Data not provided by club" />
              ) : (
                `€${formatNumberToKAndM(clubData?.team?.averageWagePerWeek)}`
              )}
            </LabelNumber>
          </PlayerClubInfoData>
          {type === "candidate" && (
            <PlayerClubInfoData className="player-descriptive-data">
              <LabelNumber className="descriptive-large-text">predicted Attack contribution uplift </LabelNumber>
              <LabelNumber className="descriptive-large-value" style={perfomaceuplift}>
                {" "}
                {clubData != null && clubData.performanceUplift && clubData.performanceUplift > 0  ? '+' : ''}
                {clubData != null && clubData.performanceUplift ? clubData.performanceUplift : 0}%
              </LabelNumber>
            </PlayerClubInfoData>
          )}
          {type === "candidate" && (
            <>
            <PlayerClubInfoData className="player-descriptive-data">
              <LabelNumber className="descriptive-large-text">predicted System fit uplift</LabelNumber>
              <div style={{display:'flex',alignItems:'center'}}>
                  <LabelNumber className="descriptive-large-value" style={systemFit}>
                    {clubData != null && (clubData.playerChemistryWithSystemFit.newSystemFit - clubData.playerChemistryWithSystemFit.oldSystemFit) > 0  ? '+' : ''}
                    {clubData != null ? clubData.playerChemistryWithSystemFit.newSystemFit - clubData.playerChemistryWithSystemFit.oldSystemFit : 0}{" "}</LabelNumber>
                  &nbsp;
                  <LabelNumber className="descriptive-large-text">(From {clubData.playerChemistryWithSystemFit.oldSystemFit} to {clubData.playerChemistryWithSystemFit.newSystemFit})</LabelNumber>
              </div>
            </PlayerClubInfoData>

            <PlayerClubInfoData className="player-descriptive-data">
              <LabelNumber className="descriptive-large-text">stylistic fit uplift</LabelNumber>
              <div style={{display:'flex',alignItems:'center'}}>
                  <LabelNumber className="descriptive-large-value" style={stylistic}>
                    {clubData?.stylisticFitAbsoluteDiffInPercentage! > 0 ? '+' : ''}{clubData?.stylisticFitAbsoluteDiffInPercentage!}{clubData?.stylisticFitAbsoluteDiffInPercentage! !== null && "%"}{" "}
                    </LabelNumber>
                  &nbsp;
                  {/* <LabelNumber className="descriptive-large-text">(From {clubData.playerChemistryWithSystemFit.oldSystemFit} to {clubData.playerChemistryWithSystemFit.newSystemFit})</LabelNumber> */}
              </div>
            </PlayerClubInfoData>
            </>
          )}
        </div>
        {isOpen && (
          <>
            <div className="stat-table-container">
              <div className="stat-table-column">
                <TableRow className="stat-table-row">
                  <LabelText className="selected-zone-name">Total costs per season</LabelText>
                  <LabelNumber className="club-info-value">
                    {clubFinancialData?.totalCostPerSeasonText === null || clubFinancialData?.totalCostPerSeason === 0 ? (
                      <EmptyDataButton label="Data not provided by club" />
                    ) : (
                      `€${clubFinancialData?.totalCostPerSeasonText}`
                    )}
                  </LabelNumber>
                </TableRow>
                <TableRow className="stat-table-row">
                  <LabelText className="selected-zone-name">Total REVENUES per season</LabelText>
                  <LabelNumber className="club-info-value">
                    {clubFinancialData?.totalRevenuePerSeasonText === null || clubFinancialData?.totalRevenuePerSeason === 0 ? (
                      <EmptyDataButton label="Data not provided by club" />
                    ) : (
                      `€${clubFinancialData?.totalRevenuePerSeasonText}`
                    )}
                  </LabelNumber>
                </TableRow>
                <TableRow className="stat-table-row">
                  <LabelText className="selected-zone-name">Profit/loss</LabelText>
                  <LabelNumber className="club-info-value" style={valueStyle}>
                    {clubFinancialData?.profitLossText === null || clubFinancialData?.profitLoss === 0 ? (
                      <EmptyDataButton label="Data not provided by club" />
                    ) : (
                      `€${clubFinancialData?.profitLossText}`
                    )}
                  </LabelNumber>
                </TableRow>
              </div>
              <div className="stat-table-column">
                <TableRow className="stat-table-row">
                  <LabelText className="selected-zone-name club-earnings-text">Earnings before interest & taxes (EBIT)</LabelText>
                  <LabelNumber className="club-info-value">
                    {clubFinancialData?.earningsBeforeITText === null || clubFinancialData?.earningsBeforeIT === 0 ? (
                      <EmptyDataButton label="Data not provided by club" />
                    ) : (
                      `€${clubFinancialData?.earningsBeforeITText}`
                    )}
                  </LabelNumber>
                </TableRow>
                <TableRow className="stat-table-row">
                  <LabelText className="selected-zone-name">Earnings before taxes (EBT)</LabelText>
                  <LabelNumber className="club-info-value">
                    {clubFinancialData?.earningsBeforeTaxesText === null || clubFinancialData?.earningsBeforeTaxes === 0 ? (
                      <EmptyDataButton label="Data not provided by club" />
                    ) : (
                      `€${clubFinancialData?.earningsBeforeTaxesText}`
                    )}
                  </LabelNumber>
                </TableRow>
                <TableRow className="stat-table-row">
                  <LabelText className="selected-zone-name">TAXES</LabelText>
                  <LabelNumber className="club-info-value">
                    {clubFinancialData?.taxesText === null || clubFinancialData?.taxes === 0 ? (
                      <EmptyDataButton label="Data not provided by club" />
                    ) : (
                      `€${clubFinancialData?.taxesText}`
                    )}
                  </LabelNumber>
                </TableRow>
              </div>
            </div>
            {type === "candidate" && <ModulePitch clubData={clubData} playerData={playerData} />}
          </>
        )}
      </PlayerCardData>
      {displayCustomNotification && (
        <PWNotification
          message={customNotificationContent}
          setIsNotification={() => setDisplayCustomNotification(false)}
          isDisplayCloseIcon={true}
          className="player-filter-notification add-similar-notification"
        />
      )}
      </>
  );
};

export default ClubsInfo;
